"use client";

import BackgroundVideo from "@/components/molecules/BackgroundVideo";
import { Button } from "@/components/ui/button";
import { BG_VIDEO_KEY_FEATURES } from "@/constants/video";
import { SLIDER_CONTENT } from "@/features/home/constants";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useState } from "react";

export default function HomeKeyFeatures() {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [prevIndex, setPrevIndex] = useState<number>(0); // Track previous index to determine direction

  // Function to handle slide change
  const handleSlideChange = (index: number) => {
    setPrevIndex(currentIndex); // Update the previous index
    setCurrentIndex(index); // Update the current index
  };

  return (
    <section id="key-features" className="w-full mx-auto">
      <div className="flex flex-col gap-8">
        <div className="flex justify-center items-center">
          <Button className="btn-default max-h-8 h-8 !min-h-8 cursor-default">
            Key Features
          </Button>
        </div>
        <h4 className="!font-normal text-center text-2xl sm:text-4xl">
          Smart AI Agents: Automate, <br />
          Optimize, and Accelerate Your Workflow
        </h4>
        {/* Slider */}
        <AnimatePresence initial={false}>
          <div className="flex relative slider-content justify-center flex-col lg:flex-row gap-5 border-x border-dashed border-base-divider key-features-wrapper pt-5">
            <div className="relative flex justify-center items-center flex-1 border-b lg:border-r lg:border-b-0 border-dashed border-base-divider">
              <BackgroundVideo
                src={BG_VIDEO_KEY_FEATURES}
                className="!relative !z-[1] !rounded-none"
              />
              <motion.div className="absolute top-0 left-0 w-full h-full !rounded-none flex justify-center flex-col items-center">
                <AnimatePresence initial={false}>
                  <motion.div
                    key={SLIDER_CONTENT[currentIndex].id}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={{
                      initial: {
                        y: currentIndex > prevIndex ? "100%" : "-100%",
                      }, // Determine direction
                      animate: { y: 0 },
                      exit: {
                        y: currentIndex > prevIndex ? "-100%" : "100%",
                        opacity: 0,
                      },
                    }}
                    transition={{
                      duration: 0.5,
                      type: "spring", // Add spring animation
                      stiffness: 150, // Controls the spring's tightness
                      damping: 25, // Controls the bounce effect
                    }}
                    className="z-[1] rounded-3xl overflow-hidden shadow-09 ease-linear absolute h-[80%] lg:w-[72%] lg:h-auto aspect-square"
                  >
                    <Image
                      src={SLIDER_CONTENT[currentIndex].image}
                      alt={SLIDER_CONTENT[currentIndex].title}
                      fill
                    />
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </div>
            <motion.div
              initial="initial"
              animate="animate"
              exit="exit"
              variants={{
                initial: { opacity: 0 },
                animate: { opacity: 1, position: "relative" },
                exit: { opacity: 0, position: "absolute" },
              }}
              transition={{ duration: 0.5 }}
              className="w-full flex-col flex flex-1"
            >
              {SLIDER_CONTENT.map((slider, index) => (
                <div
                  key={slider.id}
                  className={clsx(
                    "slide-item--item before:w-[calc(100%+40px)] before:h-px before:border-t before:absolute before:border-dashed before:left-[-20px] before:first:border-t-0 before:z-[1]",
                    currentIndex === index && "active",
                  )}
                >
                  <div className="slide-item--title border-dashed lg:border-l border-base-divider relative">
                    <div
                      className="flex items-center gap-4 slide-item--wrapper"
                      // Update current index on click
                      onClick={() => handleSlideChange(index)}
                    >
                      {slider.icon && (
                        <div
                          className={clsx(
                            "slider-icon rounded-full flex justify-center items-center !p-2 btn-default aspect-square",
                          )}
                        >
                          {slider.icon}
                        </div>
                      )}
                      <div
                        className={clsx(
                          "slider-title w-[calc(100%-40px)] text-base-primary leading-caption-m tracking-caption-m text-xs font-medium",
                        )}
                      >
                        {slider.title}
                      </div>
                    </div>

                    {currentIndex === index && (
                      <div
                        id="description"
                        className="slide-content wysiwyg-content pt-2 text-xs"
                        dangerouslySetInnerHTML={{
                          __html: slider.description,
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </AnimatePresence>
        {/* <div className="relative w-full min-h-[674px] overflow-hidden ">
        </div> */}
      </div>
    </section>
  );
}
